import React, {createContext, useContext, useReducer} from "react";
import {currentUploadBatchesReducer} from '../components/uploads/currentUploadBatches';

const initialState = {
    uploadingAssets: [],
    currentBatches: {},
    currentBatchScope: null,
    fileAdds: 0,
    disconnected: false,
    networkOffline: false,
    pausedUploadGuids: [],
    transferRetry: null,
    uploads: {}, // array of Files by Upload ID
    currentUploadSession: null, // Current upload session with state
    uploadState: 'active', // active, paused, stalled, completed, cancelled
    stalledAssets: {} // Map of asset ID to last progress timestamp
}

const UploadsStateContext = createContext(initialState);
const UploadsDispatchContext = createContext(initialState);

const reducer = (state, action) => {
    state = {...state, currentBatches: currentUploadBatchesReducer(state.currentBatches, action)};
    // console.log('UploadsContext reducer:', action.type, action);

    let key
    switch (action.type) {
        case 'restoreCurrentUploadBatches':
            return {...state, currentBatchScope: action.scope};
        case 'addFiles':
            key = action.upload?.guid || 'new'
            window.FileQueue[key] = [...(window.FileQueue[key] || []), ...action.files]

            return {
                ...state,
                uploads: {...state.uploads, [key]: [...(state.uploads[key] || []), ...action.files]},
                fileAdds: state.fileAdds + 1
            };

        case 'filesProcessed':
            const processedFiles = new Set(action.files)
            return {
                ...state,
                uploads: {
                    ...state.uploads,
                    [action.guid]: (state.uploads[action.guid] || []).filter(file => !processedFiles.has(file))
                }
            };

        case 'addUploadingAsset':
            return {...state, uploadingAssets: state.uploadingAssets.concat(action.asset) };

        case 'addUploadingAssets':
            // ActionCable historically dispatched a single asset; normalize at the reducer boundary.
            const assets = Array.isArray(action.assets) ? action.assets : [action.assets].filter(Boolean)
            const pendingAssets = assets.filter(asset => asset.aasm_state === 'pending')
            const uploadingAssets = Array.from(
                new Map([...state.uploadingAssets, ...pendingAssets].map(asset => [asset.id, asset])).values()
            )
            return {...state, uploadingAssets};

        case 'clearUploadingAssets':
            const removedAssets = state.uploadingAssets.filter(asset => asset.upload?.guid === action.uploadGuid)
            const removedAssetIds = new Set(removedAssets.map(asset => asset.id))
            const remainingStalledAssets = {...state.stalledAssets}
            removedAssetIds.forEach(id => delete remainingStalledAssets[id])

            return {
                ...state,
                uploadingAssets: state.uploadingAssets.filter(asset => asset.upload?.guid !== action.uploadGuid),
                stalledAssets: remainingStalledAssets
            };

        case 'removeUploadingAsset':
            const stalledWithoutAsset = {...state.stalledAssets}
            delete stalledWithoutAsset[action.assetId]

            return {
                ...state,
                uploadingAssets: state.uploadingAssets.filter(asset => asset.id !== action.assetId),
                stalledAssets: stalledWithoutAsset
            };

        case 'uploadProgress':
            return {...state, uploadingAssets: state.uploadingAssets.map(asset => asset.id === action.asset.id
                ? {...asset, aasm_state: 'uploading', percent: action.percent, stats: action.stats}
                : asset)};

        case 'uploadTransferStarted':
            return {...state, uploadingAssets: state.uploadingAssets.map(asset => asset.id === action.assetId
                ? {...asset, aasm_state: 'uploading'} : asset)};

        case 'uploadTransferPaused':
            return {...state, pausedUploadGuids: action.paused
                ? [...new Set([...state.pausedUploadGuids, action.uploadGuid])]
                : state.pausedUploadGuids.filter(guid => guid !== action.uploadGuid)};

        case 'setUploadNetworkOffline':
            return {...state, networkOffline: action.offline};

        case 'retryUploadTransfers':
            return {...state, transferRetry: {guid: action.uploadGuid, sequence: (state.transferRetry?.sequence || 0) + 1}};

        case 'triageNewFiles':
            key = action.upload?.guid
            const newFiles = state.uploads.new || []
            window.FileQueue[key] = [...(window.FileQueue[key] || []), ...newFiles]

            return {
                ...state,
                uploads: {...state.uploads, [key]: [...(state.uploads[key] || []), ...newFiles], new: null},
                fileAdds: state.fileAdds + 1,
                uploadState: action.upload?.upload_state || state.uploadState
            };

        case 'cancelNewFiles':
            window.FileQueue[null] = []
            state.uploads[null] = []
            state.uploads.new = []

            return {...state, uploads: {...state.uploads}};

        case 'disconnected':
            return {...state, disconnected: true}

        case 'reconnected':
            return {...state, disconnected: false}

        case 'setUploadSession':
            return {...state, currentUploadSession: action.session}

        case 'setUploadState':
            return {...state, uploadState: action.uploadState}

        case 'markAssetStalled':
            const stalledAssets = {...state.stalledAssets}
            stalledAssets[action.assetId] = Date.now()
            return {...state, stalledAssets}

        case 'clearStalledAssets':
            return {...state, stalledAssets: {}}

        case 'updateUploadProgress':
            // Update last progress timestamp for asset
            const updatedStalled = {...state.stalledAssets}
            if (action.assetId && updatedStalled[action.assetId]) {
                delete updatedStalled[action.assetId]
            }
            return {...state, stalledAssets: updatedStalled}

        default:
            return state
    }
}

const UploadsProvider = ({children})=> {
    const [state, dispatch] = useReducer(reducer, initialState);
    return (
        <UploadsStateContext.Provider value={state}>
            <UploadsDispatchContext.Provider value={dispatch}>
                {children}
            </UploadsDispatchContext.Provider>
        </UploadsStateContext.Provider>
    )
}

function useUploadsState() {
    const context = useContext(UploadsStateContext)
    if (context === undefined) {
        throw new Error('useUploadsState must be used within a Provider')
    }
    return context
}

function useUploadsDispatch() {
    const context = useContext(UploadsDispatchContext)
    if (context === undefined) {
        throw new Error('useUploadsDispatch must be used within a Provider')
    }
    return context
}

export {
    UploadsProvider,
    useUploadsState,
    useUploadsDispatch,
    reducer,
    initialState
};
